// Authentication service
// Integrates with OAuth2-proxy endpoints

import env from '@config/env';
import { isProjectMember, extractRoles } from '@config/app-roles';
import type { User } from '@/types';

interface UserInfoResponse {
  email?: string;
  preferred_username?: string;
  roles?: string[];
  groups?: string[];
}

const authService = {
  /**
   * Build the URL oauth2-proxy should redirect back to after login.
   * Preserves the full requested page (origin, path, query string and hash).
   */
  getLoginRedirectUrl(currentLocation: Pick<Location, 'href'> = window.location): string {
    return currentLocation.href;
  },

  /**
   * Build the Azure AD logout URL that oauth2-proxy should redirect to.
   */
  getLogoutRedirectUrl(currentLocation: Pick<Location, 'origin'> = window.location): string {
    return `https://login.microsoftonline.com/6ee535f2-3064-4ac9-81d8-4ceb2ff790c6/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(currentLocation.origin)}`;
  },

  /**
   * Get current user information from OAuth2-proxy
   * @returns User information including email, roles, and access level
   */
  async getUser(): Promise<User> {
    try {
      const response = await fetch(`${env.oauthEndpoint}/userinfo`, {
        credentials: 'include', // Include cookies
      });

      if (response.status === 401 || response.status === 403) {
        // User is not authenticated
        return {
          email: null,
          roles: [],
          accessLevel: 'anonymous',
          isAuthenticated: false,
          isProjectMember: false,
        };
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch user info: ${response.statusText}`);
      }

      const data: UserInfoResponse = await response.json();

      // Extract roles from Azure Entra ID App Roles (oauth2-proxy returns 'roles' array)
      // Roles are defined in App Registration and assigned in Enterprise Application
      const userRoles = data.roles || data.groups || [];  // Fallback to groups for backward compatibility

      const userIsProjectMember = isProjectMember(userRoles);
      const roles = extractRoles(userRoles);

      return {
        email: data.email || data.preferred_username || null,
        roles: userRoles,  // Return the actual roles from Azure AD
        displayRoles: roles,  // Filtered/formatted roles for display
        accessLevel: userIsProjectMember ? 'projectMember' : 'authenticated',
        isAuthenticated: true,
        isProjectMember: userIsProjectMember,
      };
    } catch (error) {
      console.error('Error fetching user info:', error);
      throw error;
    }
  },

  /**
   * Redirect to OAuth2-proxy login page
   */
  login(): void {
    const returnUrl = authService.getLoginRedirectUrl();
    window.location.href = `${env.oauthEndpoint}/start?rd=${encodeURIComponent(returnUrl)}`;
  },

  /**
   * Logout current user
   * Performs full single sign-out from both oauth2-proxy session and Azure AD
   */
  logout(): void {
    // Clear local session storage
    localStorage.removeItem('dim-session-timeout');
    // Build Azure AD logout URL with redirect back to the app origin.
    const azureLogoutUrl = authService.getLogoutRedirectUrl();
    // OAuth2-proxy sign_out clears local session, then redirects to Azure AD logout.
    window.location.href = `${env.oauthEndpoint}/sign_out?rd=${encodeURIComponent(azureLogoutUrl)}`;
  },
};

export default authService;
